<template>
    <div
        class="assignment-edit-header"
        v-if="assignment"
        :class="!groupIn && 'organizational-header'">
        <h3
            class="title"
            :class="groupIn && 'order-1'">
            <div
                class="name"
                :class="inlineEditInProgress && 'editing'">
                <span class="assignment-title">
                    {{ assignment.name }}
                    <b-button
                        class="btn-edit btn-cancel"
                        v-if="canEditPortfolio"
                        variant="primary-icon-only"
                        @click="inlineEditInProgress = true">
                    </b-button>
                </span>
                <div
                    class="inline-assignment-title-edit"
                    v-if="inlineEditInProgress && canEditPortfolio">
                    <b-form-input v-model="assignment.name"></b-form-input>
                    <b-button
                        class="btn-checked btn-update"
                        variant="success-icon-only"
                        @click="updateAssignmentTitle">
                    </b-button>
                    <b-button
                        class="btn-times btn-cancel"
                        variant="primary-icon-only"
                        @click="cancelAssignmentTitleUpdate">
                    </b-button>
                </div>
                <div
                    class="badges assignment-badges"
                    v-if="assignment.assignment_tags.length || portfolioIn.worldmap">
                    <b-badge
                        v-if="assignment.country"
                        pill
                        :style="cssColorVars(portfolioIn.categories[0].color)">
                        {{ worldmapData.countries[assignment.country].country }}
                    </b-badge>

                    <b-badge
                        v-for="tag of assignment.assignment_tags"
                        pill
                        :style="cssColorVars(tag.color)"
                        :key="`${tag.name}-${tag.id}`">
                        {{ tag.name }}
                    </b-badge>
                </div>
            </div>
            <div
                class="deadline"
                v-if="assignment.hasDeadline()">
                <i class="fad fa-alarm-exclamation icon"></i>{{ assignment.deadlines[0].agenda_item.full_date_time }}
            </div>
        </h3>

        <div
            class="assignment-edit-tools order-0"
            v-if="groupIn">
            <div
                class="group-pair-status"
                v-b-tooltip:hover
                v-if="assignment.assignment_cooperations.length > 0"
                :title="$t('assignment.collaborate_active')"
                role="button"
                @click="openTeacherCoop()">
                <i class="icon fa fa-users fa-fw"></i>
            </div>

            <span
                class="assignment-rating-status"
                id="assignment-rating-status"
                @click.prevent.stop="onPublish()"
                :class="assignmentPublishedClass">
            </span>

            <b-button
                class="btn-view-assignment"
                v-b-modal="'assignment-edit-preview-modal'"
                v-b-tooltip.hover
                variant="primary-icon-only"
                :title="$t('assignment.view_assignment')">
                <span class="fa-layers fa-fw view-assignment">
                    <i class="fad fa-file file-icon"></i>
                    <i
                        class="fal fa-search loop-icon"
                        data-fa-transform="shrink-14"></i>
                </span>
            </b-button>

            <b-tooltip
                target="assignment-rating-status"
                :title="publishToolTip"></b-tooltip>

            <div class="context-nav context-nav-main-assignment">
                <b-dropdown
                    class="ellipsis-nav assignment-nav"
                    no-caret
                    variant="primary"
                    right
                    toggle-class="assignment-edit-context-menu">
                    <template v-slot:button-content>
                        <i class="fas fa-ellipsis-v"></i>
                    </template>
                    <b-dropdown-item
                        :class="assignment.isPublished() ? 'btn-eye-closed' : 'btn-eye'"
                        @click.prevent.stop="onPublish()">
                        {{
                            assignment.isPublished()
                                ? $t('assignment.make_assignment_invisible')
                                : $t('assignment.publish_assignment')
                        }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-edit btn-edit-assignment-title"
                        v-b-modal="'assignment-name-edit-modal'"
                        v-if="canEditPortfolio"
                        @click="$event.stopPropagation()">
                        {{
                            portfolioIn.worldmap
                                ? $t('assignment.change_name_and_country')
                                : $t('assignment.change_name')
                        }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="update-selected-tag"
                        v-if="availableTags.length > 0 && canEditPortfolio"
                        @click.prevent.stop="showSelectTagsModal">
                        <i class="fas fa-tag"></i
                        >{{
                            assignment.assignment_tags.length > 0 ? $t('assignment.add_tag') : $t('assignment.add_tag')
                        }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-medal btn-assignment-rating-settings"
                        v-b-modal="'assignment-edit-rating'"
                        v-if="canEditPortfolio"
                        @click="$event.stopPropagation()">
                        {{ $t('assignment.set_assessment') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-assignment-deadline"
                        v-b-modal="'assignment-edit-deadline-modal'"
                        v-if="canEditPortfolio"
                        @click="$event.stopPropagation()">
                        <i class="btn-clock"></i>{{ $t('assignment.edit_deadline') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-assignment-cooperation"
                        @click="
                            $event.stopPropagation();
                            onClickCooperation();
                        ">
                        <i class="btn-users"></i>{{ $t('assignment.collaborate') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-assignment-rubric"
                        v-if="!assignment.assignment_rubrics.length && !assignment.assignment_skill_tree"
                        @click="openRubricModal">
                        <i class="btn-add"></i>{{ $t('assignment.add_rubrics') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                        class="btn-assignment-rubric"
                        v-else
                        @click="openRubricModal">
                        <i class="btn-edit"></i>{{ $t('assignment.edit_rubrics') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-remove-assignment-rubric"
                        v-if="!!assignment.assignment_rubrics.length || !!assignment.assignment_skill_tree"
                        @click.prevent.stop="arRemove">
                        <i class="btn-remove"></i>{{ $t('assignment.remove_rubrics') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                        class="btn-remove-assignment"
                        v-if="canEditPortfolio"
                        @click.prevent.stop="onRemove">
                        <i class="btn-remove"></i>{{ $t('assignment.delete_assignment') }}
                    </b-dropdown-item>
                </b-dropdown>
            </div>

            <design-2021-assignment-edit-cooperation-modal-component
                :assignment-in="assignment"
                @openTeacherCoop="openTeacherCoop"
                :group-in="groupIn">
            </design-2021-assignment-edit-cooperation-modal-component>

            <design-2021-assignment-edit-match-maker-modal-component
                :group-id-in="groupIn.id"
                ref="match-maker-modal">
            </design-2021-assignment-edit-match-maker-modal-component>

            <design-2021-assignment-edit-deadline-modal-component
                :assignment-in="assignment"
                :group-in="groupIn">
            </design-2021-assignment-edit-deadline-modal-component>

            <design-2021-assignment-rubric-modal-component
                :assignment-in="assignment"
                :groupIn="groupIn"
                ref="add-assignment-rubric"
                @rubrics-added="addRubrics($event)">
            </design-2021-assignment-rubric-modal-component>

            <design-2021-assignment-edit-skill-tree-modal-component
                :assignment-in="assignment"
                :group-in="groupIn"
                ref="add-assignment-skill-tree"
                @skill-tree-changed="changeSkillTree($event)">
            </design-2021-assignment-edit-skill-tree-modal-component>

            <design-2021-portfolio-edit-rating-modal-component
                :object-in="assignment"
                grade-target="sub_assignments"
                key-name="assignment-edit-rating"
                :show-grade-options="true"
                :connections="[{ type: 'subAssignments', name: 'Deelopdrachten' }]"
                :title="$t('assignment.rate_assignment') + ': ' + assignment.name"
                :title-weight="$t('assignment.rate_sub_assignment_weight')"
                @on-save="saveRating($event)">
            </design-2021-portfolio-edit-rating-modal-component>

            <design-2021-portfolio-assignment-tag-select-modal-component
                ref="assignmentSelectedTagModal"
                :group-id="groupIn.id"
                :available-tags="availableTags"
                @stored-tag="updateAssignmentTag">
            </design-2021-portfolio-assignment-tag-select-modal-component>
        </div>

        <div
            class="organizational-tools"
            v-else>
            <div class="context-nav context-nav-main-assignment">
                <b-dropdown
                    class="ellipsis-nav assignment-nav"
                    no-caret
                    variant="primary"
                    right
                    toggle-class="assignment-edit-context-menu">
                    <template v-slot:button-content>
                        <i class="fas fa-ellipsis-v"></i>
                    </template>
                    <b-dropdown-item
                        class="btn-edit-assignment-title"
                        v-b-modal="'assignment-edit-preview-modal'">
                        <i class="btn-open"></i>{{ $t('assignment.view_assignment') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-edit btn-edit-assignment-title"
                        v-b-modal="'assignment-name-edit-modal'">
                        {{
                            portfolioIn.worldmap
                                ? $t('assignment.change_name_and_country')
                                : $t('assignment.change_name')
                        }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-remove-assignment"
                        @click.prevent.stop="onRemove">
                        <i class="btn-remove"></i>{{ $t('assignment.delete_assignment') }}
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>

        <design-2021-assignment-edit-name-edit-modal-component
            :portfolio-in="portfolioIn"
            :assignment-in="assignment"
            @assignment-map-update="updateMapAssignment"
            @setup-continent="setupContinent">
        </design-2021-assignment-edit-name-edit-modal-component>

        <design-2021-assignment-edit-preview-modal-component
            :assignment-in="assignment"
            :group-type-id-in="groupIn?.type_id">
        </design-2021-assignment-edit-preview-modal-component>

        <design-2021-portfolio-edit-publishing-modal-component
            :group-in="groupIn"
            :ords-in="true"
            @update-publishing="updatePublishing"
            ref="assignment-publishing-modal">
        </design-2021-portfolio-edit-publishing-modal-component>
    </div>
</template>

<script>
import { ords } from '../../../app';
import { worldmapData } from '../../../helpers/worldmapData';

export default {
    name: 'design-2021-assignment-edit-title-buttons-component',
    props: ['assignmentIn', 'groupIn', 'portfolioIn', 'canEditPortfolio'],
    data() {
        return {
            availableTags: [],
            assignment: this.assignmentIn,
            inlineEditInProgress: false,
            initialTitle: this.assignmentIn.name,
        };
    },

    mounted() {
        this.axios.get(this.routes.getassignmentTags(this.groupIn.id)).then(
            (response) => {
                if (response.data.length > 0) {
                    this.availableTags = response.data.map((tag) => {
                        return {
                            id: tag.id,
                            name: tag.name,
                            color: tag.color,
                        };
                    });
                }
            },
            (error) => {
                this.alert.ajaxError(error);
            },
        );
    },

    methods: {
        addRubrics(assignmentRubrics) {
            this.assignment.assignment_rubric = assignmentRubrics;
        },

        changeSkillTree(assignmentSkillTree) {
            this.assignment.assignment_skill_tree = assignmentSkillTree;
        },

        openRubricModal() {
            if (this.assignment.assignment_skill_tree) {
                // in this order
                this.$refs['add-assignment-skill-tree'].show();
                return;
            }
            if (this.assignment.assignment_rubric) {
                // in this order
                this.$refs['add-assignment-rubric'].show();
                return;
            }
            this.alert
                .multipleChoice(
                    this.$t('assignment.make_a_choice'),
                    this.$t(
                        'assignment.do_you_want_to_link_specific_rubrics_to_this_assignment_or_skill_set_where_students_can_choose_the_skill_themselves',
                    ),
                    {
                        skillTree: {
                            text: this.$t('assignment.skills_set'),
                            value: 'skillTree',
                            closeModal: true,
                            visible: true,
                            className: 'btn btn-primary',
                        },
                        rubrics: {
                            text: this.$t('assignment.specific_rubrics'),
                            value: 'rubrics',
                            closeModal: true,
                            visible: true,
                            className: 'btn btn-primary',
                        },
                    },
                )
                .then((reaction) => {
                    if (reaction === 'skillTree') {
                        this.$refs['add-assignment-skill-tree'].show();
                    }
                    if (reaction === 'rubrics') {
                        this.$refs['add-assignment-rubric'].show();
                    }
                });
        },

        openTeacherCoop(assignmentCooperation = this.assignmentIn.assignment_cooperations[0]) {
            this.$refs['match-maker-modal'].show(assignmentCooperation);
        },

        onClickCooperation() {
            if (this.assignment.hasSubAssignmentWithAssignmentCooperation()) {
                this.alert.notice(
                    this.$t('assignment.collaborate'),
                    this.$t(
                        'assignment.the_groups_tool_is_already_used_on_this_group_this_is_not_possible_in_combination_with_the_collaboration_tool',
                    ),
                );
            } else {
                this.$bvModal.show('assignment-edit-cooperation-modal');
            }
        },

        onRemove() {
            this.alert
                .confirm(
                    this.$t('assignment.delete_assignment'),
                    this.$t('assignment.are_you_sure_you_want_to_delete_this_assignment'),
                )
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving();

                        this.axios.delete(this.routes.assignmentDelete(this.assignment.id)).then(
                            (response) => {
                                this.alert.success();
                                window.location.href =
                                    this.routes.portfolio() + '/edit/' + this.portfolioIn.id + '/' + this.groupIn.id;
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },
        arRemove() {
            if (this.assignment.assignment_skill_tree) {
                this.alert
                    .confirm(
                        this.$t('assignment.expand_skills_set'),
                        this.$t('assignment.are_you_sure_you_want_to_disable_the_link_to_the_skill_set'),
                    )
                    .then((confirm) => {
                        if (confirm) {
                            this.alert.saving();

                            this.axios
                                .delete(this.routes.assignmentSkillTree(this.assignmentIn.id, this.groupIn.id))
                                .then(
                                    (response) => {
                                        this.assignment.assignment_skill_tree = null;
                                        this.alert.success();
                                    },
                                    (error) => {
                                        this.alert.ajaxError(error);
                                    },
                                );
                        }
                    });
            }
            if (this.assignment.assignment_rubrics.length) {
                this.alert
                    .confirm(
                        this.$t('assignment.expand_rubrics'),
                        this.$t('assignment.are_you_sure_you_want_to_disable_the_link_to_rubrics'),
                    )
                    .then((confirm) => {
                        if (confirm) {
                            this.alert.saving();

                            this.axios
                                .delete(this.routes.assignmentRubricRemove(this.assignmentIn.id, this.groupIn.id))
                                .then(
                                    (response) => {
                                        this.assignment.assignment_rubric = null;
                                        this.alert.success();
                                    },
                                    (error) => {
                                        this.alert.ajaxError(error);
                                    },
                                );
                        }
                    });
            }
        },

        onPublish() {
            this.$refs['assignment-publishing-modal'].show(this.assignment);
        },

        updatePublishing(publishing) {
            if (publishing) {
                this.assignment.addRelation('publishings', publishing);
            }
            this.alert.success();
        },

        saveRating(data) {
            this.alert.saving();

            if (!this.assignment.portfolio_rating) {
                this.axios.post(this.routes.portfolioAssignmentRating(), data).then(
                    (response) => {
                        ords.hold();

                        this.assignmentIn.addRelation('portfolio_rating', response.data);

                        for (const subAssignment of this.assignment.sub_assignments) {
                            for (const subAssignmentResponse of response.data.sub_assignments) {
                                if (
                                    !subAssignmentResponse.portfolio_rating ||
                                    subAssignment.id !== subAssignmentResponse.id
                                ) {
                                    continue;
                                }

                                if (subAssignment.portfolio_rating) {
                                    subAssignment.portfolio_rating.update(subAssignmentResponse.portfolio_rating);
                                } else {
                                    subAssignment.addRelation(
                                        'portfolio_rating',
                                        subAssignmentResponse.portfolio_rating,
                                    );
                                }
                            }
                        }

                        ords.continue();

                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            } else {
                this.axios.post(this.routes.portfolioAssignmentRatingUpdate(), data).then(
                    (response) => {
                        ords.hold();

                        this.assignmentIn.portfolio_rating.update(response.data.rating);

                        for (const subAssignment of this.assignment.sub_assignments) {
                            for (const subAssignmentResponse of response.data.sub_assignments) {
                                if (
                                    !subAssignmentResponse.portfolio_rating ||
                                    subAssignment.id !== subAssignmentResponse.id
                                ) {
                                    continue;
                                }

                                if (subAssignment.portfolio_rating) {
                                    subAssignment.portfolio_rating.update(subAssignmentResponse.portfolio_rating);
                                } else {
                                    subAssignment.addRelation(
                                        'portfolio_rating',
                                        subAssignmentResponse.portfolio_rating,
                                    );
                                }
                            }
                        }

                        ords.continue();

                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            }
        },

        showSelectTagsModal() {
            this.$refs.assignmentSelectedTagModal.show(this.assignment);
        },

        updateAssignmentTag(tag) {
            if (!tag) {
                return;
            }

            this.$set(this.assignment, 'assignment_tags', tag);
        },

        cssColorVars(color) {
            return {
                '--tagColor': color,
            };
        },

        updateAssignmentTitle() {
            if (this.assignment.name === null || this.assignment.name === '') {
                this.alert.error('Titel is niet bijgewerkt', 'De titel mag niet leeg zijn');
                return;
            }

            this.alert.notice();

            const data = {
                name: this.assignment.name,
            };

            this.axios.post(this.routes.assignmentUpdate(this.assignmentIn.id), data).then(
                (response) => {
                    this.assignmentIn.update({ name: this.assignment.name });
                    this.initialTitle = this.assignment.name;
                    this.inlineEditInProgress = false;
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        cancelAssignmentTitleUpdate() {
            this.assignment.name = this.initialTitle;
            this.inlineEditInProgress = false;
        },

        updateMapAssignment(data) {
            this.assignment.name = data.name;
            this.assignment.country = data.country;
            this.assignment.color = data.color;
        },

        setupContinent() {
            this.$emit('setup-continent');
        },
    },

    watch: {
        assignmentIn: function (value) {
            this.assignment = value;
        },
    },
    computed: {
        worldmapData() {
            return worldmapData;
        },
        publishToolTip() {
            if (this.assignment.publishings.length) {
                if (new Date(this.assignment.publishings[0].published_at).getTime() <= new Date().getTime()) {
                    return this.$t('assignment.assignment_is_published');
                } else {
                    let dateObject = new Date(this.assignment.publishings[0].published_at);
                    let dateString = new Intl.DateTimeFormat('nl', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                    }).format(dateObject);
                    return this.$t('assignment.assignment_is_published') + dateString;
                }
            } else {
                return this.$t('assignment.assignment_is_visible');
            }
        },
        assignmentPublishedClass() {
            if (this.assignment.publishings.length) {
                if (new Date(this.assignment.publishings[0].published_at).getTime() <= new Date().getTime()) {
                    return 'btn btn-primary-icon-only btn-eye';
                } else {
                    return 'btn btn-warning-icon-only btn-clock';
                }
            } else {
                return 'btn btn-warning-icon-only btn-eye-closed';
            }
        },
    },
};
</script>

<style scoped lang="scss">
.assignment-edit-header {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    flex: 1 0 100%;
    row-gap: map-get($grid-gutter-spacing, 'eight');
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');

    .title {
        font-weight: $font-weight-bold;
        margin-bottom: 0;
        flex: 1 0 100%;

        .name {
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
            align-items: flex-start;
            justify-content: flex-start;
            column-gap: $grid-gutter-width;
            row-gap: map-get($grid-gutter-spacing, 'four');

            &.editing {
                .assignment-title {
                    display: none;
                    opacity: 0;
                    width: 0;
                    height: 0;
                    left: -100%;
                }

                .inline-assignment-title-edit {
                    opacity: 1;
                }
            }
        }

        .deadline {
            margin-top: map-get($grid-gutter-spacing, 'eight');
        }
    }

    .assignment-title,
    .inline-assignment-title-edit {
        display: flex;
        align-items: center;
        transition: all 0.8s ease;
    }

    .assignment-title {
        position: relative;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
        opacity: 1;
        width: auto;
        height: auto;

        .btn-update-title {
            &:before {
                margin: 0;
            }
        }
    }

    .inline-assignment-title-edit {
        column-gap: map-get($grid-gutter-spacing, 'eight');
        opacity: 0;
        width: 100%;

        input {
            margin: 0;
            border: 1px solid $primary;
            padding: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'fifteen');
        }

        .btn {
            font-size: 1.125rem;

            &:before {
                margin: 0;
            }
        }
    }

    .deadline {
        color: $danger;
        font-size: $base-font-size;

        .icon {
            margin-right: map-get($grid-gutter-spacing, 'eight');
        }
    }

    .assignment-edit-tools {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        flex: 1 0 100%;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');

        .group-pair-status {
            .icon {
                color: var(--primary);
            }
        }

        .deadline {
            margin-right: auto;
        }
    }

    .assignment-rating-status {
        font-size: 1.3125rem;

        &:before {
            margin-right: 0;
        }

        &.btn-warning-icon:hover {
            background-color: $warning;
        }

        &.btn-warning-icon:hover {
            background-color: $warning;
        }

        &:hover {
            cursor: pointer;
        }
    }

    .view-assignment {
        position: relative;
        font-size: 2.0625rem;
        line-height: 1;
        height: 40px;
        width: 36px;

        .file-icon {
            font-size: 2.5rem;
            color: var(--primary);
        }

        .loop-icon {
            position: absolute;
            display: block;
            top: 13px;
            left: 5px;
            color: $white;
            font-size: 1.25rem;
        }
    }

    .context-nav-main-assignment ::v-deep {
        .assignment-nav {
            button.dropdown-toggle {
                color: $white !important;
                background-color: var(--primary);
                padding: map-get($grid-gutter-spacing, 'eight') map-get($grid-gutter-spacing, 'fifteen');
            }

            .btn-eye,
            .btn-eye-closed,
            .btn-assignment-rating-settings,
            .btn-assignment-cooperation,
            .btn-assignment-deadline,
            .btn-edit-assignment-title,
            .update-selected-tag {
                i,
                &:before {
                    color: var(--primary);
                }
            }

            .btn-assignment-cooperation,
            .btn-assignment-deadline,
            .btn-remove-assignment,
            .btn-edit-assignment-title {
                font-size: $default-font-size;

                .dropdown-item {
                    display: flex;
                    align-items: center;

                    i {
                        margin-right: map-get($grid-gutter-spacing, 'eight');
                        font-size: $base-font-size;
                    }
                }

                &:before {
                    margin-right: map-get($grid-gutter-spacing, 'eight');
                }
            }

            .dropdown-menu {
                border: 1px solid $border-color;

                li {
                    &:before,
                    i {
                        min-width: 20px;
                    }
                }
            }
        }
    }
}

.organizational-header {
    .title {
        width: calc(100% - 51px);
        align-self: center;
    }

    .organizational-tools {
        align-self: baseline;
        display: flex;
        align-items: center;
        margin-left: auto;
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .assignment-edit-header {
        flex-wrap: nowrap;

        .title {
            flex: unset;

            &:has(+ .assignment-edit-tools > *:first-child:nth-last-child(1)) {
                width: calc(100% - 50px);
            }

            &:has(+ .assignment-edit-tools > *:first-child:nth-last-child(2)) {
                width: calc(100% - 105px);
            }

            &:has(+ .assignment-edit-tools > *:first-child:nth-last-child(3)) {
                width: calc(100% - 145px);
            }

            &:has(+ .assignment-edit-tools > *:first-child:nth-last-child(4)) {
                width: calc(100% - 185px);
            }
        }

        .assignment-edit-tools {
            order: 1;
            flex: 0 1 auto;
            margin-left: auto;
            align-self: baseline;
        }

        .inline-assignment-title-edit {
            width: 100%;
        }
    }
}
</style>
